import { SolanaAddress } from "@solomon-apps/shared";

import type { useVerifyActionFlow } from "@/verification";

import type { FindPositionStatus, VerifyMessageTone, VerifyStatus } from "../steps";
import type { VerifiedSource } from "../types";

import type { usePositionSource } from "./usePositionSource";

interface VerificationPresentationOptions {
    source: ReturnType<typeof usePositionSource>;
    verification: ReturnType<typeof useVerifyActionFlow>;
    verificationError: string | null;
    issuerName: string | null;
    verifiedSource: VerifiedSource | null;
    address: string;
    connectedWalletAddress: string;
    alreadyRegistered: boolean;
    verifyStatus: VerifyStatus;
    retryReady: boolean;
    findStatus: FindPositionStatus;
    sourceResolving: boolean;
}
interface UseVerificationPresentationResult {
    addressError: string | null;
    addressInvalid: boolean;
    addressLabel: string;
    addressPlaceholder: string;
    findMessage: string | null;
    findMessageTone: "error" | "muted";
    sourceNotice: string | null;
    verifyMessage: string | null;
    verifyMessageTone: "error" | "muted";
    verificationIsDirect: boolean;
    verifyDirectLabel: string;
    verifyActionDisabled: boolean;
    verifyActionLabel: string;
    showSourceRetry: boolean;
    verifyDirectDisabled: boolean;
}

export function useVerificationPresentation({
    source,
    verification,
    verificationError,
    issuerName,
    verifiedSource,
    address,
    connectedWalletAddress,
    alreadyRegistered,
    verifyStatus,
    retryReady,
    findStatus,
    sourceResolving
}: VerificationPresentationOptions): UseVerificationPresentationResult {
    const {
        sourceInput,
        sourceDetection,
        sourceKind,
        resolvedMultisig,
        memberCanVerify,
        membershipCheckPending,
        membershipUnknown,
        walletNeedsSwitch
    } = source;
    // The field's own error line: one message per reason the pasted source
    // can't be used, shown under the input (which takes the error rail).
    const addressError: string | null =
        sourceInput.length === 0 ? null : sourceDetection.status === "invalid" ? "This isn't a valid address." : null;
    const addressInvalid = addressError !== null;

    const addressLabel = "Address";
    const addressPlaceholder = "Paste a wallet, Squads multisig, or vault address";

    // Discovery outcome messages surfaced inside the combined verify step (the
    // discovery runs automatically after address classification).
    let findMessage: string | null = null;
    let findMessageTone: VerifyMessageTone = "muted";
    if (findStatus === "not-covered") {
        // TODO(deprecate): "not covered" only exists because a position must be
        // bound to a Program surface when the address is added. Once addresses
        // are registered independently of Programs (Address -> Participation
        // model: the address is monitored as soon as it is declared, and joins
        // eligible Programs automatically, like retail's
        // RetailEnrollmentPositionService.reconcile), this stop, the discovery
        // gate before verify, and the surface-bound sign message go away.
        // Keep it until the backend supports that.
        findMessage = `Your account needs to be set up before this address can be added. Please contact ${issuerName?.trim() || "your distributor"}.`;
        findMessageTone = "error";
    } else if (findStatus === "failed") {
        // A verification click can retry failed automatic discovery.
        findMessage = "Address check failed.";
        findMessageTone = "error";
    }

    // The auto micro-transfer fallback is mid-flight: signing wasn't possible,
    // so we've switched to the on-chain transfer. Surface a non-blocking line
    // so the wallet's transfer-approval prompt doesn't appear out of nowhere.
    const isMicroTransferActive =
        verification.verificationMethod === "microTransfer" &&
        (verification.step === "walletSign" || verification.step === "confirming");

    let verifyMessage: string | null = null;
    let verifyMessageTone: VerifyMessageTone = "muted";
    if (verificationError) {
        verifyMessage = verificationError;
        verifyMessageTone = "error";
    } else if (verification.step === "error") {
        // Failed state carries no verbose error line — the button label
        // ("Verification Failed", then "Retry") is sufficient on its own.
    } else if (verifiedSource !== null) {
        // The field badge carries the outcome (Verified, or Pending
        // Verification for a direct choice), so there is no line here.
        verifyMessage = null;
    } else if (sourceKind === "eoa") {
        // No before-click wallet messages — the wallet is only involved after
        // the user presses Verify Ownership.
    } else {
        switch (sourceDetection.status) {
            case "eoa":
                break;
            case "empty":
                verifyMessage = "Paste the address this position lives at.";
                break;
            case "invalid":
                // Said under the field (`addressError`), not beside the action.
                break;
            case "loading":
                verifyMessage = "Verifying address type...";
                break;
            case "unsupported":
                // No verify message — the field-level "unsupported address
                // type" line below the input covers it.
                break;
            case "unsupported-version":
                // Said under the field (`addressError`), not beside the action.
                break;
            case "lookup-failed":
                // Rendered as a "try again" retry link in VerifyWalletGroup
                // (showSourceRetry), so no plain-string message here.
                break;
            case "multisig-squads":
                if (!connectedWalletAddress) {
                    verifyMessage = `Resolved ${sourceDetection.multisig.memberCount} signer${sourceDetection.multisig.memberCount === 1 ? "" : "s"}. Connect a member wallet to verify.`;
                } else if (membershipCheckPending) {
                    verifyMessage = "Checking connected wallet membership...";
                } else if (membershipUnknown) {
                    verifyMessage =
                        "Membership could not be fully checked here; the API will assert it when you submit.";
                }
                // can-verify / cannot-verify / not-member carry no button-side
                // message: can-verify needs none, and the two non-signer cases
                // relabel the button to "Switch Wallet" and surface the switch
                // hint via the connected-wallet line below the field (see
                // VerifyWalletGroup), mirroring the EOA mismatch.
                break;
        }
    }

    // Drop the non-essential helper / success status lines — the verify button
    // already conveys idle / verifying / verified. Only keep error-tone
    // messages (e.g. the direct-Solomon success note keeps its own tone).
    if (verifyMessageTone !== "error") {
        verifyMessage = null;
    }

    // An already-registered source carries no line here or under the field:
    // the "Already Registered" badge and the field's error state say it, and
    // both verify actions are disabled.

    // The resolving line and the auto micro-transfer status both replace the
    // account-type badge below the input — not button-side
    // messages. "onchain" one word, per house style.
    const sourceNotice = isMicroTransferActive
        ? "Signing not possible. Attempting to verify through a tiny onchain transfer..."
        : sourceResolving
          ? "Checking address..."
          : null;

    // Discovery runs automatically; verification can also await or retry it.
    // This gate asks whether the connected wallet can act as the source.
    const canStartVerification =
        sourceKind === "eoa"
            ? connectedWalletAddress.length > 0 && (!address || address === connectedWalletAddress)
            : sourceKind === "multisig-squads" &&
              resolvedMultisig !== null &&
              connectedWalletAddress.length > 0 &&
              memberCanVerify &&
              !membershipCheckPending;
    const verificationIsDirect = verifiedSource?.verificationMode === "direct-solomon";
    const verifyDirectLabel = `Verify Directly with ${issuerName?.trim() || "Solomon"}`;
    const verifyActionDisabled =
        alreadyRegistered ||
        verificationIsDirect ||
        verifyStatus === "verified" ||
        // On failure the button is inert for the 3s "Verification Failed" hold, then becomes
        // an enabled "Retry".
        (verifyStatus === "failed" && !retryReady) ||
        verifyStatus === "verifying" ||
        sourceKind === null ||
        // A wallet that can't act as the source is NOT disabled — the button
        // becomes an actionable "Switch Wallet" (EOA mismatch or multisig
        // non-signer), which disconnects and reopens the picker.
        (connectedWalletAddress.length > 0 && !canStartVerification && !walletNeedsSwitch) ||
        (sourceKind === "multisig-squads" && connectedWalletAddress.length === 0 && resolvedMultisig === null);
    const verifyActionLabel = verificationIsDirect
        ? "Verify Ownership"
        : verifyStatus === "failed"
          ? retryReady
              ? "Retry"
              : "Verification Failed"
          : verifyStatus === "verifying"
            ? "Verifying..."
            : verifyStatus === "verified" && !verificationIsDirect
              ? "Verified"
              : connectedWalletAddress.length === 0
                ? sourceKind === null
                    ? "Verify Source"
                    : sourceKind === "eoa"
                      ? "Connect Wallet"
                      : "Connect Member Wallet"
                : walletNeedsSwitch
                  ? "Switch Wallet"
                  : sourceKind === null
                    ? "Verify Source"
                    : "Verify Ownership";
    const showSourceRetry = sourceDetection.status === "lookup-failed";
    const verifyDirectDisabled =
        alreadyRegistered ||
        verifyStatus === "verified" ||
        verifyStatus === "verifying" ||
        sourceDetection.status === "loading" ||
        !SolanaAddress.isShape(sourceInput);

    return {
        addressError,
        addressInvalid,
        addressLabel,
        addressPlaceholder,
        findMessage,
        findMessageTone,
        sourceNotice,
        verifyMessage,
        verifyMessageTone,
        verificationIsDirect,
        verifyDirectLabel,
        verifyActionDisabled,
        verifyActionLabel,
        showSourceRetry,
        verifyDirectDisabled
    };
}
